/**
 * SceneObjectManager: runtime owner of the unified `SceneObject` list.
 *
 * Builds an in-world marker for each object (via markerFactory), and each frame
 * evaluates triggers by `interaction.mode`:
 *   - enter / auto : player enters the trigger volume → fire events
 *   - lookAt        : crosshair dwells on the marker → fire events
 *   - click / pressE: fired on demand by onClick()/onPressE()
 *
 * Triggering emits OBJECT_TRIGGER (carrying the object's actions) for the
 * UI/main dispatcher to act on. Portal objects additionally emit PORTAL_ENTER
 * (so the existing main.ts switchTo path handles teleport) — this keeps portal
 * behavior identical whether the object came from legacy `portals[]` or `objects`.
 * The destination scene manifest owns the arrival spawn.
 *
 * `pick(state)` does a ray-sphere test against every object's marker and returns
 * the nearest id — used by the editor for click-to-select.
 *
 * Legacy HotspotManager/TriggerZoneManager/PortalManager remain for old
 * `hotspots[]`/`triggerZones[]`/`portals[]` arrays; this manager handles the
 * converted `objects` list. A scene normally routes through only one path
 * (objects present ⇒ legacy arrays are empty after conversion).
 */

import { Quat, Vec3 } from 'playcanvas';
import type { Application, Entity } from 'playcanvas';
import { eventBus } from '../events/EventBus';
import type { Collision } from '../collision';
import type { CameraState } from '../player/types';
import type { SceneObject, SceneEventAction } from './SceneObjectTypes';
import { createMarkerFromConfig } from './markerFactory';
import { createSceneObjectCollision } from './SceneObjectCollision';
import { VideoScreenSystem } from './VideoScreenSystem';
import type { VideoControlState } from './VideoScreenSystem';

const fwd = new Vec3();
const toCenter = new Vec3();
const quat = new Quat();
// Reusable scratch quaternions / axes for local-axis auto-rotation of imagePanel markers.
const baseRotQuat = new Quat();
const axisRotQuat = new Quat();
const localRotQuat = new Quat();
const LOCAL_AXIS_X = new Vec3(1, 0, 0);
const LOCAL_AXIS_Y = new Vec3(0, 1, 0);
const LOCAL_AXIS_Z = new Vec3(0, 0, 1);
const MOBILE_VIDEO_UPLOAD_RAY_DIST = 45;
const MOBILE_VIDEO_UPLOAD_NEAR_DIST = 18;
const MOBILE_VIDEO_UPLOAD_DIST_SQ = MOBILE_VIDEO_UPLOAD_NEAR_DIST * MOBILE_VIDEO_UPLOAD_NEAR_DIST;

interface SceneObjectManagerOptions {
    deferMediaAssets?: boolean;
}

type DeferredMediaKind = 'sprite' | 'video' | 'glb' | 'gsplat';
type AutoRotationState = { angleOffset: number };

export class SceneObjectManager {
    private _app: Application;
    private _baseCollision: Collision | null;
    private _runtimeCollision: Collision | null;
    private _objects: SceneObject[];
    private _markers = new Map<string, Entity>();
    private _deferredMediaIds = new Set<string>();
    private _deferMediaAssets: boolean;
    private _inside = new Set<string>();
    private _lookDwell = new Map<string, number>();
    private _autoFired = new Set<string>();
    private _autoRotationStates = new Map<string, AutoRotationState>();
    private _runtimeVisibleVideoIds = new Set<string>();
    private _videoScreens: VideoScreenSystem;

    constructor(app: Application, objects: SceneObject[], collision: Collision | null, options: SceneObjectManagerOptions = {}) {
        this._app = app;
        this._objects = objects;
        this._deferMediaAssets = Boolean(options.deferMediaAssets);
        this._baseCollision = collision;
        this._runtimeCollision = null;
        this._rebuildRuntimeCollision();
        this._videoScreens = new VideoScreenSystem(app);
        for (const o of objects) this._buildMarker(o);
    }

    get objects(): readonly SceneObject[] { return this._objects; }

    get collision(): Collision | null { return this._runtimeCollision; }

    get deferredMediaCount(): number { return this._deferredMediaIds.size; }

    /** Get an object by id. */
    get(id: string): SceneObject | undefined {
        return this._objects.find((o) => o.id === id);
    }

    /** The in-world marker entity for an object (editor gizmo attaches to this). */
    getMarkerEntity(id: string): Entity | undefined {
        return this._markers.get(id);
    }

    /** Add an object at runtime (editor placement). Builds its marker.
     *  If the id already exists, auto-generates a unique one to prevent
     *  selection bugs caused by duplicate ids. */
    add(obj: SceneObject): void {
        if (this._objects.some((o) => o.id === obj.id)) {
            let n = 2;
            const base = obj.id;
            while (this._objects.some((o) => o.id === `${base}-${n}`)) n++;
            obj.id = `${base}-${n}`;
        }
        this._objects.push(obj);
        this._buildMarker(obj);
        this._rebuildRuntimeCollision();
    }

    /** Remove an object (editor delete). */
    remove(id: string): void {
        const idx = this._objects.findIndex((o) => o.id === id);
        if (idx < 0) return;
        this._objects.splice(idx, 1);
        const m = this._markers.get(id);
        this._deferredMediaIds.delete(id);
        this._autoRotationStates.delete(id);
        this._videoScreens.detach(id);
        if (m) {
            this._app.root.removeChild(m);
            m.destroy();
            this._markers.delete(id);
        }
        this._inside.delete(id);
        this._lookDwell.delete(id);
        this._autoFired.delete(id);
        this._rebuildRuntimeCollision();
    }

    /** Rebuild an object's marker after a marker/transform edit. */
    refreshMarker(id: string): void {
        const obj = this.get(id);
        if (!obj) return;
        const old = this._markers.get(id);
        this._videoScreens.detach(id);
        this._deferredMediaIds.delete(id);
        this._autoRotationStates.delete(id);
        if (old) {
            this._app.root.removeChild(old);
            old.destroy();
        }
        this._buildMarker(obj);
        this._rebuildRuntimeCollision();
    }

    loadDeferredMediaAssets(kinds?: readonly DeferredMediaKind[]): number {
        const allowed = kinds ? new Set<DeferredMediaKind>(kinds) : null;
        if (!allowed) this._deferMediaAssets = false;
        let loaded = 0;
        for (const id of [...this._deferredMediaIds]) {
            const obj = this.get(id);
            if (!obj || this._markers.has(id)) {
                this._deferredMediaIds.delete(id);
                continue;
            }
            const kind = this._deferredMediaKind(obj);
            if (allowed && (!kind || !allowed.has(kind))) continue;
            this._buildMarker(obj, false);
            loaded++;
        }
        if (this._deferredMediaIds.size === 0) this._deferMediaAssets = false;
        return loaded;
    }

    setBaseCollision(collision: Collision | null): void {
        this._baseCollision = collision;
        this._rebuildRuntimeCollision();
    }

    private _rebuildRuntimeCollision(): void {
        this._runtimeCollision = createSceneObjectCollision(
            this._baseCollision,
            () => this._objects,
            (id) => this._markers.get(id),
            (object) => object.type !== 'videoScreen' || this._isVideoScreenRuntimeVisible(object)
        );
    }

    private _isDeferredMediaObject(o: SceneObject): boolean {
        return o.marker.kind === 'glb' || o.marker.kind === 'gsplat' || o.marker.kind === 'sprite' || o.marker.kind === 'video' || Boolean(o.video);
    }

    private _deferredMediaKind(o: SceneObject): DeferredMediaKind | null {
        if (o.marker.kind === 'sprite') return 'sprite';
        if (o.marker.kind === 'video' || Boolean(o.video)) return 'video';
        if (o.marker.kind === 'glb') return 'glb';
        if (o.marker.kind === 'gsplat') return 'gsplat';
        return null;
    }

    private _buildMarker(obj: SceneObject, allowDefer = this._deferMediaAssets) {
        if (allowDefer && this._isDeferredMediaObject(obj)) {
            this._deferredMediaIds.add(obj.id);
            return;
        }
        this._deferredMediaIds.delete(obj.id);
        const e = createMarkerFromConfig(this._app, obj.marker);
        const t = obj.transform;
        e.setPosition(t.position[0], t.position[1], t.position[2]);
        e.setLocalEulerAngles(t.rotation[0], t.rotation[1], t.rotation[2]);
        this._app.root.addChild(e);
        this._markers.set(obj.id, e);
        this._videoScreens.attach(obj, e);
    }

    /** Set an object's world transform (editor gizmo/property panel). */
    setTransform(id: string, position: [number, number, number], rotation: [number, number, number]) {
        const obj = this.get(id);
        const m = this._markers.get(id);
        if (!obj || !m) return;
        obj.transform.position = position;
        obj.transform.rotation = rotation;
        m.setPosition(position[0], position[1], position[2]);
        m.setLocalEulerAngles(rotation[0], rotation[1], rotation[2]);
    }

    /** Apply a full transform (pos/rot/scale) to an object's data AND marker
     *  entity in one step, without rebuilding the marker. Used by gizmo drags
     *  and history undo/redo (rebuilding would detach the gizmo mid-session).
     *  `marker.scale` is kept in sync so the property panel + pick radius stay
     *  correct (the marker entity's localScale is the visual source of truth). */
    applyTransform(id: string, position: [number, number, number], rotation: [number, number, number], scale: [number, number, number]) {
        const obj = this.get(id);
        const m = this._markers.get(id);
        if (!obj || !m) return;
        obj.transform.position = position;
        obj.transform.rotation = rotation;
        obj.transform.scale = scale;
        if (obj.marker.kind === 'primitive' || obj.marker.kind === 'video' || obj.marker.kind === 'imagePanel') obj.marker.scale = scale;
        else obj.marker.scale = scale[0];
        // Trigger zones: the box volume IS the scale, so keep trigger.size in
        // sync (gizmo scale replaces the old property-panel size input).
        if (obj.type === 'triggerZone' && obj.trigger) obj.trigger.size = scale;
        m.setPosition(position[0], position[1], position[2]);
        m.setLocalEulerAngles(rotation[0], rotation[1], rotation[2]);
        m.setLocalScale(scale[0], scale[1], scale[2]);
    }

    // ── Per-frame trigger evaluation ─────────────────────────────────────
    update(state: CameraState, dt: number) {
        let videoActive = false;
        for (const o of this._objects) {
            if (!this._isControllableVideo(o)) continue;
            const visible = this._isVideoUploadCandidate(state, o);
            this._videoScreens.setVisible(o.id, visible);
            videoActive ||= visible;
        }
        this._videoScreens.update();
        this._updateAutoRotatingMarkers(dt);
        const autoRotateActive = this._hasActiveAutoRotatingMarkers();
        const p = state.position;
        for (const o of this._objects) {
            const inter = o.interaction;
            if (!inter) continue;

            // 'auto' fires once on first update.
            if (inter.mode === 'auto') {
                if (!this._autoFired.has(o.id)) {
                    this._autoFired.add(o.id);
                    this._fire(o);
                }
                continue;
            }

            if (inter.mode === 'enter') {
                const inside = this._isInside(p, o);
                const was = this._inside.has(o.id);
                if (inside && !was) {
                    this._inside.add(o.id);
                    this._fire(o);
                } else if (!inside && was) {
                    this._inside.delete(o.id);
                    // hideScene is a zone enter/exit toggle: restore on exit.
                    if (this._hasHideSceneAction(o)) {
                        eventBus.emit({ type: 'SCENE_VISIBILITY', visible: true, objectId: o.id });
                    }
                }
                continue;
            }

            if (inter.mode === 'lookAt') {
                // Crosshair ray vs marker sphere; dwell ~0.4s to fire.
                const hit = this._raySphere(state, o, 60);
                const dwell = hit ? (this._lookDwell.get(o.id) ?? 0) + dt : 0;
                this._lookDwell.set(o.id, dwell);
                if (hit && dwell >= 0.4 && !this._inside.has(o.id)) {
                    this._inside.add(o.id);
                    this._fire(o);
                } else if (!hit) {
                    this._inside.delete(o.id);
                }
                continue;
            }
        }
        return videoActive || autoRotateActive;
    }

    getVideoControlState(state: CameraState): VideoControlState | null {
        const target = this._focusedVideoScreen(state);
        if (!target) return null;
        return this._videoScreens.getState(target.id);
    }

    toggleVideoPlayback(state: CameraState): void {
        const target = this._focusedVideoScreen(state);
        if (!target) return;
        this._showVideoScreen(target);
        this._videoScreens.togglePlayback(target.id);
    }

    showFocusedVideoScreen(state: CameraState): void {
        const target = this._focusedVideoScreen(state);
        if (!target) return;
        this._showVideoScreen(target);
    }

    hideFocusedVideoScreen(state: CameraState): void {
        const target = this._focusedVideoScreen(state);
        if (!target) return;
        this._hideVideoScreen(target);
    }

    toggleVideoMuted(state: CameraState): void {
        const target = this._focusedVideoScreen(state);
        if (!target) return;
        this._videoScreens.toggleMuted(target.id);
    }

    seekVideo(state: CameraState, time: number): void {
        const target = this._focusedVideoScreen(state);
        if (!target) return;
        this._videoScreens.seek(target.id, time);
    }

    /** Pause all in-world video screens that are currently playing; returns
     *  their IDs so they can be restored via resumeVideoScreens(). */
    pauseAllVideoScreens(): string[] {
        return this._videoScreens.pauseAllPlaying();
    }

    /** Resume the video screens whose IDs were returned by pauseAllVideoScreens. */
    resumeVideoScreens(ids: string[]): void {
        this._videoScreens.resume(ids);
    }

    /** Click trigger: fire the nearest click-mode object under the crosshair. */
    onClick(state: CameraState): string | null {
        return this._fireNearest(state, 'click') ?? this._fireVideoScreenClick(state);
    }

    /** Ray-based click trigger: fire the nearest click-mode object hit by an
     *  arbitrary world-space ray (mouse cursor). `dx,dy,dz` should be normalized. */
    onClickRay(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number): string | null {
        return this._fireNearestRay(ox, oy, oz, dx, dy, dz, 'click') ?? this._fireVideoScreenClickRay(ox, oy, oz, dx, dy, dz);
    }

    /** Press-E trigger: fire nearest pressE-mode object under crosshair, or
     *  the nearest one the player is standing inside (fallback). */
    onPressE(state: CameraState): string | null {
        const hit = this._fireNearest(state, 'pressE');
        if (hit) return hit;
        // Fallback: nearest pressE object the player is inside.
        let best: string | null = null;
        let bestDist = Infinity;
        for (const o of this._objects) {
            if (o.interaction?.mode !== 'pressE') continue;
            const d = this._distSq(state.position, o);
            const r = (o.interaction.radius ?? 3);
            if (d <= r * r && d < bestDist) { bestDist = d; best = o.id; }
        }
        if (best) this._fireById(best);
        return best;
    }

    /** Editor pick: nearest object of any type under the crosshair. */
    pick(state: CameraState, maxDist = 60): string | null {
        quat.setFromEulerAngles(state.angles);
        quat.transformVector(Vec3.FORWARD, fwd);
        return this.pickRay(
            state.position.x, state.position.y, state.position.z,
            fwd.x, fwd.y, fwd.z,
            maxDist
        );
    }

    /** Editor pick: nearest object hit by an arbitrary world-space ray (cursor).
     *  `dx,dy,dz` should be normalized. Returns the nearest id within maxDist. */
    pickRay(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxDist = 200): string | null {
        let bestId: string | null = null;
        let bestT = Infinity;
        for (const o of this._objects) {
            const r = this._pickRadius(o);
            const cx = o.transform.position[0] - ox;
            const cy = o.transform.position[1] - oy;
            const cz = o.transform.position[2] - oz;
            // Ray-sphere: |origin + t*dir - center|^2 = r^2. dir is unit ⇒ a = 1.
            const b = 2 * (dx * cx + dy * cy + dz * cz);
            const c = cx * cx + cy * cy + cz * cz - r * r;
            const disc = b * b - 4 * c;
            if (disc < 0) continue;
            const t = (-b - Math.sqrt(disc)) / 2;
            if (t < 0 || t > maxDist) continue;
            if (t < bestT) { bestT = t; bestId = o.id; }
        }
        return bestId;
    }

    // ── Internals ────────────────────────────────────────────────────────
    private _fireNearest(state: CameraState, mode: string): string | null {
        quat.setFromEulerAngles(state.angles);
        quat.transformVector(Vec3.FORWARD, fwd);
        return this._fireNearestRay(state.position.x, state.position.y, state.position.z, fwd.x, fwd.y, fwd.z, mode);
    }

    private _fireNearestRay(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, mode: string): string | null {
        let bestId: string | null = null;
        let bestT = Infinity;
        for (const o of this._objects) {
            if (o.interaction?.mode !== mode) continue;
            const r = this._pickRadius(o);
            const cx = o.transform.position[0] - ox;
            const cy = o.transform.position[1] - oy;
            const cz = o.transform.position[2] - oz;
            const t = dx * cx + dy * cy + dz * cz;
            if (t < 0 || t > 60) continue;
            const px = ox + dx * t;
            const py = oy + dy * t;
            const pz = oz + dz * t;
            const rx = px - o.transform.position[0];
            const ry = py - o.transform.position[1];
            const rz = pz - o.transform.position[2];
            if (rx * rx + ry * ry + rz * rz <= r * r && t < bestT) {
                bestT = t;
                bestId = o.id;
            }
        }
        if (bestId) this._fireById(bestId);
        return bestId;
    }

    private _fire(o: SceneObject) { this._fireById(o.id); }

    /** Whether the object carries a hideScene action (zone enter/exit toggle). */
    private _hasHideSceneAction(o: SceneObject): boolean {
        const events = o.interaction?.events;
        return Boolean(events && events.some((e) => e.type === 'hideScene'));
    }

    private _fireById(id: string) {
        const o = this.get(id);
        if (!o) return;
        const actions: SceneEventAction[] = o.interaction?.events ?? [];

        // Portal objects drive teleport via PORTAL_ENTER (existing switchTo path).
        if (o.portal) {
            eventBus.emit({
                type: 'PORTAL_ENTER',
                id: o.id,
                name: o.name,
                targetRoomId: o.portal.targetRoomId, targetScene: o.portal.targetScene
            });
        }
        if (actions.length > 0) {
            eventBus.emit({ type: 'OBJECT_TRIGGER', objectId: o.id, name: o.name, actions });
        }
    }

    private _isInside(p: Vec3, o: SceneObject): boolean {
        const inter = o.interaction;
        if (!inter) return false;
        // Box trigger (triggerZone) uses trigger.size; else sphere radius.
        if (o.type === 'triggerZone' && o.trigger) {
            const c = o.transform.position;
            const s = o.trigger.size;
            return (
                Math.abs(p.x - c[0]) <= s[0] / 2 &&
                Math.abs(p.y - c[1]) <= s[1] / 2 &&
                Math.abs(p.z - c[2]) <= s[2] / 2
            );
        }
        const r = inter.radius ?? 3;
        return this._distSq(p, o) <= r * r;
    }

    private _raySphere(state: CameraState, o: SceneObject, maxDist: number): boolean {
        quat.setFromEulerAngles(state.angles);
        quat.transformVector(Vec3.FORWARD, fwd);
        const r = this._pickRadius(o);
        toCenter.set(
            o.transform.position[0] - state.position.x,
            o.transform.position[1] - state.position.y,
            o.transform.position[2] - state.position.z
        );
        const t = fwd.dot(toCenter);
        if (t < 0 || t > maxDist) return false;
        const cx = state.position.x + fwd.x * t;
        const cy = state.position.y + fwd.y * t;
        const cz = state.position.z + fwd.z * t;
        const dx = cx - o.transform.position[0];
        const dy = cy - o.transform.position[1];
        const dz = cz - o.transform.position[2];
        return dx * dx + dy * dy + dz * dz <= r * r;
    }

    private _focusedVideoScreen(state: CameraState): SceneObject | null {
        return this._rayVideoScreen(state) ?? this._nearestVideoScreen(state.position);
    }

    private _fireVideoScreenClick(state: CameraState): string | null {
        const target = this._rayVideoScreen(state);
        if (!target?.video) return null;
        return this._emitVideoScreenClick(target);
    }

    private _fireVideoScreenClickRay(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number): string | null {
        const target = this._rayVideoScreenRay(ox, oy, oz, dx, dy, dz);
        if (!target?.video) return null;
        return this._emitVideoScreenClick(target);
    }

    private _emitVideoScreenClick(target: SceneObject): string | null {
        const videoUrl = this._selectVideoScreenUrl(target);
        if (!videoUrl.trim()) return null;
        const action: SceneEventAction = {
            id: `${target.id}-open-video`,
            type: 'playVideo',
            name: target.name,
            payload: {
                title: target.name,
                videoUrl: this._selectVideoScreenUrl(target)
            }
        };
        eventBus.emit({ type: 'OBJECT_TRIGGER', objectId: target.id, name: target.name, actions: [action] });
        return target.id;
    }

    private _rayVideoScreenRay(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxDist = 60): SceneObject | null {
        let best: SceneObject | null = null;
        let bestT = Infinity;
        for (const o of this._objects) {
            if (!this._isControllableVideo(o)) continue;
            if (!this._isVideoScreenRuntimeVisible(o)) continue;
            const r = this._pickRadius(o);
            const cx = o.transform.position[0] - ox;
            const cy = o.transform.position[1] - oy;
            const cz = o.transform.position[2] - oz;
            const t = dx * cx + dy * cy + dz * cz;
            if (t < 0 || t > maxDist) continue;
            const px = ox + dx * t;
            const py = oy + dy * t;
            const pz = oz + dz * t;
            const rx = px - o.transform.position[0];
            const ry = py - o.transform.position[1];
            const rz = pz - o.transform.position[2];
            if (rx * rx + ry * ry + rz * rz <= r * r && t < bestT) {
                best = o;
                bestT = t;
            }
        }
        return best;
    }

    private _selectVideoScreenUrl(target: SceneObject): string {
        const video = target.video;
        if (!video) return '';
        const mobileRuntime = document.getElementById('app-root')?.classList.contains('mobile-runtime') ??
            window.matchMedia('(pointer: coarse)').matches;
        const mobileUrl = video.mobileUrl?.trim() ?? '';
        return mobileRuntime && mobileUrl ? mobileUrl : video.url;
    }

    private _rayVideoScreen(state: CameraState, maxDist = 60): SceneObject | null {
        quat.setFromEulerAngles(state.angles);
        quat.transformVector(Vec3.FORWARD, fwd);
        let best: SceneObject | null = null;
        let bestT = Infinity;
        for (const o of this._objects) {
            if (!this._isControllableVideo(o)) continue;
            if (!this._isVideoScreenRuntimeVisible(o)) continue;
            const r = this._pickRadius(o);
            toCenter.set(
                o.transform.position[0] - state.position.x,
                o.transform.position[1] - state.position.y,
                o.transform.position[2] - state.position.z
            );
            const t = fwd.dot(toCenter);
            if (t < 0 || t > maxDist) continue;
            const cx = state.position.x + fwd.x * t;
            const cy = state.position.y + fwd.y * t;
            const cz = state.position.z + fwd.z * t;
            const dx = cx - o.transform.position[0];
            const dy = cy - o.transform.position[1];
            const dz = cz - o.transform.position[2];
            if (dx * dx + dy * dy + dz * dz <= r * r && t < bestT) {
                best = o;
                bestT = t;
            }
        }
        return best;
    }

    private _nearestVideoScreen(p: Vec3): SceneObject | null {
        let best: SceneObject | null = null;
        let bestDist = Infinity;
        for (const o of this._objects) {
            if (!this._isControllableVideo(o)) continue;
            const d = this._distSq(p, o);
            if (d < bestDist) {
                best = o;
                bestDist = d;
            }
        }
        return best;
    }

    private _isControllableVideo(o: SceneObject): boolean {
        if (o.type !== 'videoScreen') return false;
        return this._videoScreens.getState(o.id) !== null;
    }

    private _isVideoScreenRuntimeVisible(o: SceneObject): boolean {
        return o.video?.defaultVisible !== false || this._runtimeVisibleVideoIds.has(o.id);
    }

    private _showVideoScreen(target: SceneObject): void {
        this._runtimeVisibleVideoIds.add(target.id);
        this._videoScreens.show(target.id);
    }

    private _hideVideoScreen(target: SceneObject): void {
        this._runtimeVisibleVideoIds.delete(target.id);
        this._videoScreens.hide(target.id);
    }

    private _isVideoUploadCandidate(state: CameraState, o: SceneObject): boolean {
        if (!this._isVideoScreenRuntimeVisible(o)) return false;
        return this._rayVideoScreen(state, MOBILE_VIDEO_UPLOAD_RAY_DIST) === o || this._distSq(state.position, o) <= MOBILE_VIDEO_UPLOAD_DIST_SQ;
    }

    /** Pick/trigger radius: interaction.radius, else marker half-size, else 2m. */
    private _pickRadius(o: SceneObject): number {
        if (o.interaction?.radius != null) return o.interaction.radius;
        if (o.marker.kind === 'primitive' && o.marker.scale) {
            return Math.max(o.marker.scale[0], o.marker.scale[1], o.marker.scale[2]) / 2;
        }
        if (o.marker.kind === 'video' && o.marker.scale) {
            return Math.max(o.marker.scale[0], o.marker.scale[1], o.marker.scale[2]) / 2;
        }
        if (o.marker.kind === 'imagePanel' && o.marker.scale) {
            return Math.max(o.marker.scale[0], o.marker.scale[1], o.marker.scale[2]) / 2;
        }
        return 2;
    }

    private _updateAutoRotatingMarkers(dt: number): void {
        for (const obj of this._objects) {
            const entity = this._markers.get(obj.id);
            if (!entity) continue;
            if (obj.marker.kind !== 'imagePanel') {
                this._autoRotationStates.delete(obj.id);
                continue;
            }
            const marker = obj.marker;
            const base = obj.transform.rotation;
            const rotationSpeed = marker.rotationSpeed ?? 30;
            if (!marker.autoRotate || !Number.isFinite(rotationSpeed) || rotationSpeed === 0) {
                this._autoRotationStates.delete(obj.id);
                entity.setLocalEulerAngles(base[0], base[1], base[2]);
                continue;
            }
            const axis = marker.rotationAxis ?? 'y';
            const state = this._autoRotationStates.get(obj.id) ?? { angleOffset: 0 };
            state.angleOffset = (state.angleOffset + rotationSpeed * dt) % 360;
            this._autoRotationStates.set(obj.id, state);
            // Rotate around the marker's own local axis via R_base * R_axis, so the
            // spin is independent of parent/world orientation — e.g. a panel pitched
            // 90° to stand upright still spins around its own local axis, not the
            // world axis coupled through the Euler rotation order.
            baseRotQuat.setFromEulerAngles(base[0], base[1], base[2]);
            switch (axis) {
                case 'x':
                    axisRotQuat.setFromAxisAngle(LOCAL_AXIS_X, state.angleOffset);
                    break;
                case 'z':
                    axisRotQuat.setFromAxisAngle(LOCAL_AXIS_Z, state.angleOffset);
                    break;
                case 'y':
                default:
                    axisRotQuat.setFromAxisAngle(LOCAL_AXIS_Y, state.angleOffset);
                    break;
            }
            localRotQuat.mul2(baseRotQuat, axisRotQuat);
            entity.setLocalRotation(localRotQuat);
        }
    }

    private _hasActiveAutoRotatingMarkers(): boolean {
        return this._objects.some((obj) =>
            obj.marker.kind === 'imagePanel' &&
            Boolean(obj.marker.autoRotate) &&
            Number.isFinite(obj.marker.rotationSpeed ?? 30) &&
            (obj.marker.rotationSpeed ?? 30) !== 0
        );
    }

    private _distSq(p: Vec3, o: SceneObject): number {
        const c = o.transform.position;
        const dx = c[0] - p.x, dy = c[1] - p.y, dz = c[2] - p.z;
        return dx * dx + dy * dy + dz * dz;
    }

    /** Remove all markers (on scene unload). */
    destroy() {
        for (const [, m] of this._markers) {
            this._app.root.removeChild(m);
            m.destroy();
        }
        this._markers.clear();
        this._runtimeVisibleVideoIds.clear();
        this._inside.clear();
        this._lookDwell.clear();
        this._autoFired.clear();
        this._autoRotationStates.clear();
        this._videoScreens.destroy();
    }
}
